import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FlaskConical, Calendar, Clock, FileText,
  LogOut, User, CheckCircle, AlertCircle,
  Loader2, Eye, X, Printer, Building2,
  TrendingUp, TrendingDown, Minus, Activity,
  Brain, Sparkles, ChevronRight, History, BarChart2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { patientPortalAPI } from '../../services/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList, BarChart, Bar, LineChart, Line, Legend } from 'recharts';

const SERIAL_COLORS = ['#0F6E56', '#2563eb', '#ea580c', '#8b5cf6', '#ec4899'];

const SerialChartTest = ({ test, testName }) => {
  const serialResults = test.serial_results || [];
  const chartConfig = test.chart_config || {};
  const chartType = chartConfig.chart_type || 'line';
  const evaluation = test.evaluation || null;

  // Build component info from first interval
  const firstInterval = serialResults[0]?.components || [];
  const compInfoMap = {};
  firstInterval.forEach(c => {
    const id = String(c.test_component_id);
    compInfoMap[id] = {
      name: c.component_name || '',
      unit: c.unit || '',
      result_type: c.result_type || '',
      critical_value: c.critical_value ?? null,
      reference_low: c.reference_low ?? null,
      reference_high: c.reference_high ?? null,
      normal_text: c.normal_text || ''
    };
  });

  const isMeasurement = (info) => {
    if (!info) return false;
    if (info.result_type === 'single_select' || info.result_type === 'text') return false;
    if (info.reference_high == null && info.critical_value == null && !info.normal_text) return false;
    return true;
  };

  const infoComponents = firstInterval.filter(c => !isMeasurement(compInfoMap[String(c.test_component_id)]));
  const numericCompIds = firstInterval
    .filter(c => {
      const info = compInfoMap[String(c.test_component_id)];
      if (!isMeasurement(info)) return false;
      return serialResults.some(sr => (sr.components || []).some(sc => String(sc.test_component_id) === String(c.test_component_id) && sc.value_numeric != null));
    })
    .map(c => String(c.test_component_id));

  const componentNames = numericCompIds.map(id => compInfoMap[id]?.name || '-');

  // Build chart data
  const chartData = serialResults.map(sr => {
    const point = { name: sr.label || '-' };
    numericCompIds.forEach(compId => {
      const name = compInfoMap[compId]?.name || compId;
      const found = (sr.components || []).find(c => String(c.test_component_id) === compId);
      point[name] = found?.value_numeric ?? null;
    });
    return point;
  });

  // Critical thresholds
  const criticalThresholds = {};
  numericCompIds.forEach(compId => {
    const info = compInfoMap[compId];
    if (info?.critical_value != null) criticalThresholds[info.name] = info.critical_value;
  });
  const uniqueThresholds = [];
  const seenVals = new Set();
  Object.entries(criticalThresholds).forEach(([, val]) => {
    if (!seenVals.has(val)) { seenVals.add(val); uniqueThresholds.push({ value: val, label: `${val} ppm` }); }
  });

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-slate-50 border-b border-gray-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-teal-600">
          <FlaskConical className="w-5 h-5" />
        </div>
        <h3 className="font-bold text-slate-800">{testName}</h3>
      </div>

      <div className="p-6 space-y-6" dir="ltr">
        {/* Info Components */}
        {infoComponents.length > 0 && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-slate-700 text-white px-4 py-2 text-sm font-semibold">Test Information</div>
            <table className="w-full text-sm">
              <tbody>
                {infoComponents.map((c, i) => {
                  const info = compInfoMap[String(c.test_component_id)];
                  const val = c.value_text || c.value_numeric || '-';
                  return (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2.5 font-medium text-gray-700 w-2/5">{info?.name || '-'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-900">{val}{info?.unit ? ` ${info.unit}` : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Component Info Boxes */}
        {numericCompIds.length > 0 && (
          <div className="flex gap-3 justify-center flex-wrap">
            {numericCompIds.map((compId, ci) => {
              const info = compInfoMap[compId];
              const color = SERIAL_COLORS[ci % SERIAL_COLORS.length];
              return (
                <div key={ci} className="border-2 rounded-xl px-4 py-2.5 text-center flex-1 min-w-[140px] max-w-[200px] bg-white" style={{ borderColor: color }}>
                  <div className="text-sm font-semibold text-gray-800">{info?.name || '-'}</div>
                  {info?.critical_value != null && (
                    <div className="text-xs text-gray-500 mt-1">Critical: <span className="font-semibold" style={{ color }}>≥ {info.critical_value} {info.unit || 'ppm'}</span></div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Chart */}
        {chartData.length > 0 && numericCompIds.length > 0 && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-blue-600 text-white px-4 py-2 text-sm font-semibold">Results Chart</div>
            <div className="p-4 bg-white">
              <ResponsiveContainer width="100%" height={300}>
                {chartType === 'bar' ? (
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} label={chartConfig.x_axis_label ? { value: chartConfig.x_axis_label, position: 'insideBottom', offset: -2, fontSize: 11 } : undefined} />
                    <YAxis tick={{ fontSize: 11 }} label={chartConfig.y_axis_label ? { value: chartConfig.y_axis_label, angle: -90, position: 'insideLeft', offset: 4, fontSize: 11 } : undefined} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    {uniqueThresholds.map((t, i) => (
                      <ReferenceLine key={i} y={t.value} stroke="#9ca3af" strokeDasharray="8 4" strokeWidth={1.5} label={{ value: t.label, position: 'right', fontSize: 10, fill: '#6b7280' }} />
                    ))}
                    {componentNames.map((name, ci) => (
                      <Bar key={name} dataKey={name} fill={SERIAL_COLORS[ci % SERIAL_COLORS.length]} label={{ position: 'top', fontSize: 9, fontWeight: 700, fill: SERIAL_COLORS[ci % SERIAL_COLORS.length] }} />
                    ))}
                  </BarChart>
                ) : (
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} label={chartConfig.x_axis_label ? { value: chartConfig.x_axis_label, position: 'insideBottom', offset: -2, fontSize: 11 } : undefined} />
                    <YAxis tick={{ fontSize: 11 }} label={chartConfig.y_axis_label ? { value: chartConfig.y_axis_label, angle: -90, position: 'insideLeft', offset: 4, fontSize: 11 } : undefined} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    {uniqueThresholds.map((t, i) => (
                      <ReferenceLine key={i} y={t.value} stroke="#9ca3af" strokeDasharray="8 4" strokeWidth={1.5} label={{ value: t.label, position: 'right', fontSize: 10, fill: '#6b7280' }} />
                    ))}
                    {componentNames.map((name, ci) => (
                      <Line key={name} type="monotone" dataKey={name} stroke={SERIAL_COLORS[ci % SERIAL_COLORS.length]} strokeWidth={2.5}
                        dot={{ r: 5, fill: SERIAL_COLORS[ci % SERIAL_COLORS.length], stroke: '#fff', strokeWidth: 2 }}
                        label={{ position: 'top', fontSize: 9, fontWeight: 700, fill: SERIAL_COLORS[ci % SERIAL_COLORS.length] }} />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Data Table */}
        {chartData.length > 0 && numericCompIds.length > 0 && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-blue-600 text-white px-4 py-2 text-sm font-semibold">Gas Measurements Over Time</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-700 text-white">
                  <th className="px-3 py-2.5 text-center border border-gray-600">Time (min)</th>
                  {numericCompIds.map((compId, ci) => {
                    const info = compInfoMap[compId];
                    return <th key={ci} className="px-3 py-2.5 text-center border border-gray-600">{info?.name || '-'}{info?.unit ? ` (${info.unit})` : ''}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {serialResults.map((sr, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2.5 text-center border border-gray-200 font-medium">{sr.label}</td>
                    {numericCompIds.map((compId, ci) => {
                      const found = (sr.components || []).find(c => String(c.test_component_id) === compId);
                      const val = found?.value_numeric;
                      const threshold = compInfoMap[compId]?.critical_value;
                      const isExceeded = threshold != null && val != null && val >= threshold;
                      return (
                        <td key={ci} className={`px-3 py-2.5 text-center border border-gray-200 ${isExceeded ? 'font-bold bg-amber-50 text-amber-800' : ''}`}>
                          {val != null ? val : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-green-50">
                  <td className="px-3 py-2.5 text-center border border-gray-200 font-semibold text-green-800">Normal Range</td>
                  {numericCompIds.map((compId, ci) => {
                    const info = compInfoMap[compId];
                    let rangeText = '-';
                    if (info?.normal_text) rangeText = info.normal_text;
                    else if (info?.critical_value != null) rangeText = `< ${info.critical_value} ${info.unit || 'ppm'}`;
                    else if (info?.reference_low != null && info?.reference_high != null) rangeText = `${info.reference_low} - ${info.reference_high}`;
                    return <td key={ci} className="px-3 py-2.5 text-center border border-gray-200 text-green-800 font-medium text-xs">{rangeText}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Evaluation */}
        {evaluation && (
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-blue-600 text-white px-4 py-2 text-sm font-semibold">Result Evaluation</div>
            <div className="p-5">
              {evaluation.overall_result && (() => {
                const isPositive = /positive|abnormal|high/i.test(evaluation.overall_result);
                return (
                  <div className={`border-2 rounded-xl p-4 text-center mb-4 ${isPositive ? 'border-orange-400 bg-orange-50' : 'border-green-400 bg-green-50'}`}>
                    <div className="text-xs text-gray-500 mb-1 font-medium">Overall Result</div>
                    <div className={`text-xl font-bold ${isPositive ? 'text-orange-700' : 'text-green-700'}`}>{evaluation.overall_result}</div>
                  </div>
                );
              })()}
              {evaluation.evaluation_items?.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {evaluation.evaluation_items.map((item, i) => {
                    const isAbnormal = item.is_abnormal || false;
                    return (
                      <div key={i} className={`border rounded-xl p-3 ${isAbnormal ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
                        <div className="text-xs text-gray-500 mb-0.5">{item.label}</div>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-lg font-bold ${isAbnormal ? 'text-orange-700' : 'text-green-700'}`}>{item.value ?? '-'}</span>
                          {item.unit && <span className="text-xs text-gray-500">{item.unit}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PatientResults = () => {
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const tenantData = JSON.parse(localStorage.getItem('tenant') || '{}');
  const tenantLogo = tenantData?.branding?.logoUrl || '/listech-logo.svg';
  const [bookingResults, setBookingResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('results'); // 'results' or 'insights'
  const [expandedHistory, setExpandedHistory] = useState(null); // Component ID for expanded chart
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    const patientData = localStorage.getItem('patientData');
    const patientToken = localStorage.getItem('patientToken');

    if (!patientToken) {
      navigate('/login');
      return;
    }

    if (patientData) {
      setPatient(JSON.parse(patientData));
    }

    fetchBookings();
  }, [navigate]);

  const fetchBookings = async () => {
    try {
      const response = await patientPortalAPI.getBookings();
      setBookings(response.data.data || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingResults = async (bookingId) => {
    setLoadingResults(true);
    setAiError(null);
    try {
      const response = await patientPortalAPI.getBookingResults(bookingId);
      setBookingResults(response.data.data);
      setShowResultsModal(true);
      setActiveTab('results');
    } catch (err) {
      console.error('Error fetching results:', err);
    } finally {
      setLoadingResults(false);
    }
  };

  const requestAIAnalysis = async () => {
    if (!bookingResults || aiLoading) return;
    // If already cached, no need to call
    if (bookingResults.ai_analysis) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await patientPortalAPI.generateAIAnalysis(bookingResults._id);
      const aiData = response.data.data;
      setBookingResults(prev => ({ ...prev, ai_analysis: aiData.ai_analysis }));
    } catch (err) {
      const msg = err.response?.data?.message || 'حدث خطأ أثناء التحليل';
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('patientToken');
    localStorage.removeItem('patientData');
    navigate('/login');
  };

  const handlePrintReport = (bookingId) => {
    window.open(`/patient/report/${bookingId}`, '_blank');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'في الانتظار', color: 'bg-amber-100 text-amber-700', icon: Clock },
      new: { label: 'جديد', color: 'bg-blue-100 text-blue-700', icon: Clock },
      sample_collected: { label: 'تم جمع العينة', color: 'bg-purple-100 text-purple-700', icon: Activity },
      in_progress: { label: 'قيد التحليل', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
      completed: { label: 'مكتمل', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      released: { label: 'تم الإصدار', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-700', icon: AlertCircle }
    };
    return statusMap[status] || statusMap.pending;
  };

  const formatDate = (date) => {
    // Gregorian Date Format (DD/MM/YYYY)
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  // Improved calculation logic for realistic position (RTL Direction: Low=Right, High=Left)
  const calculatePosition = (value, low, high) => {
    if (!value || low === undefined || high === undefined) return 50;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 50;

    // Define the visual range:
    // Red (High Critical) | Orange (High) | Green (Normal) | Orange (Low) | Red (Low Critical)
    // Left (0%) <----------------------------------------------------------------> Right (100%)

    // Normal Range (Green): 35% to 65% of the bar width.
    // Low Limit (e.g. 280) -> Should be at 65% (Right edge of Green)
    // High Limit (e.g. 800) -> Should be at 35% (Left edge of Green)

    const range = high - low;

    // Calculate ratio of value within normal range (0 to 1)
    // 0 = Low Limit
    // 1 = High Limit
    const ratio = (numValue - low) / range;

    // Inverted Mapping Formula:
    // P = 65 - (ratio * 30)
    // if ratio=0 -> P=65 (Right edge of green)
    // if ratio=1 -> P=35 (Left edge of green)

    let percentage = 65 - (ratio * 30);

    // Keep marker within 2% and 98%
    percentage = Math.max(2, Math.min(98, percentage));

    return percentage;
  };

  const getResultStatus = (value, low, high, flag) => {
    if (flag === 'H' || flag === 'high') return { status: 'high', label: 'مرتفع', color: 'text-red-600', bgColor: 'bg-red-50' };
    if (flag === 'L' || flag === 'low') return { status: 'low', label: 'منخفض', color: 'text-orange-600', bgColor: 'bg-orange-50' };

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && low !== undefined && high !== undefined) {
      if (numValue < low) return { status: 'low', label: 'منخفض', color: 'text-orange-600', bgColor: 'bg-orange-50' };
      if (numValue > high) return { status: 'high', label: 'مرتفع', color: 'text-red-600', bgColor: 'bg-red-50' };
    }

    return { status: 'normal', label: 'طبيعي', color: 'text-teal-600', bgColor: 'bg-teal-50' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium">جاري تحميل بياناتك الصحية...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50" dir="rtl">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md sticky top-0 z-30 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/listech-logo.svg" alt="LIS Techs" className="h-10 w-auto" />
            </div>

            <div className="flex items-center gap-4">
              <div className="text-left hidden sm:block">
                <p className="font-bold text-slate-800 text-sm">{patient?.full_name}</p>
                <p className="text-slate-400 text-xs font-mono" dir="ltr">{patient?.national_id}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-teal-500/20">
                {patient?.full_name?.charAt(0) || <User className="w-5 h-5" />}
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                title="تسجيل الخروج"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-slate-800 mb-2">مرحباً، {patient?.full_name?.split(' ')[0]} 👋</h2>
          <p className="text-slate-500">إليك نظرة عامة على ملفك الصحي ونتائج تحاليلك الأخيرة</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between"
          >
            <div>
              <p className="text-slate-500 text-sm mb-1">إجمالي الزيارات</p>
              <h3 className="text-3xl font-bold text-slate-800">{bookings.length}</h3>
            </div>
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between"
          >
            <div>
              <p className="text-slate-500 text-sm mb-1">نتائج جاهزة</p>
              <h3 className="text-3xl font-bold text-slate-800">
                {bookings.filter(b => b.status === 'completed' || b.status === 'released').length}
              </h3>
            </div>
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between"
          >
            <div>
              <p className="text-slate-500 text-sm mb-1">قيد المعالجة</p>
              <h3 className="text-3xl font-bold text-slate-800">
                {bookings.filter(b => ['pending', 'in_progress', 'sample_collected'].includes(b.status)).length}
              </h3>
            </div>
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
          </motion.div>
        </div>

        {/* Visits List */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-bold text-slate-800">سجل الزيارات</h3>
          </div>

          {bookings.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FlaskConical className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-lg font-medium text-slate-800">لا توجد زيارات سابقة</h3>
              <p className="text-slate-500 mt-2">ستظهر نتائج تحاليلك هنا بمجرد إجراء الفحوصات</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {bookings.map((booking, idx) => {
                const status = getStatusBadge(booking.status);
                const StatusIcon = status.icon;
                const hasResults = booking.status === 'completed' || booking.status === 'released';

                return (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    key={booking._id}
                    className="group bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-1 h-full bg-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      {/* Left info */}
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-teal-50 flex items-center justify-center shrink-0 overflow-hidden">
                          {booking.lab_logo ? (
                            <img src={booking.lab_logo} alt={booking.lab_name} className="w-full h-full object-contain p-1.5" />
                          ) : (
                            <FlaskConical className="w-7 h-7 text-teal-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg font-bold text-slate-800">زيارة #{booking.booking_no}</span>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1.5">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {formatDate(booking.booking_date)}
                            </span>
                            {booking.lab_name && (
                              <span className="flex items-center gap-1.5 font-medium text-teal-700">
                                <Building2 className="w-4 h-4 text-teal-500" />
                                {booking.lab_name}
                              </span>
                            )}
                            {booking.branch_id && (
                              <span className="flex items-center gap-1.5">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                {booking.branch_id.branch_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={() => {
                          setSelectedBooking(booking);
                          fetchBookingResults(booking._id);
                        }}
                        disabled={loadingResults || !hasResults}
                        className={`w-full md:w-auto px-6 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${hasResults
                          ? 'bg-slate-900 text-white hover:bg-teal-600 shadow-lg shadow-slate-900/10 hover:shadow-teal-600/20'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                      >
                        {loadingResults && selectedBooking?._id === booking._id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                        {hasResults ? 'عرض التقرير' : 'غير جاهز'}
                        {hasResults && <ChevronRight className="w-4 h-4 opacity-50" />}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Results Modal */}
      <AnimatePresence>
        {showResultsModal && bookingResults && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 overflow-y-auto"
          >
            <div className="min-h-screen py-8 px-4 flex items-center justify-center">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl overflow-hidden"
              >
                {/* Modal Header */}
                <div className="px-8 py-6 border-b border-gray-100 bg-white sticky top-0 z-10 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">نتائج التحليل</h2>
                    <p className="text-slate-400 text-sm">رقم الملف: #{bookingResults.booking_no} | التاريخ: {formatDate(bookingResults.booking_date)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handlePrintReport(bookingResults._id)}
                      className="hidden md:flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                    >
                      <Printer className="w-4 h-4" />
                      طباعة
                    </button>
                    <button
                      onClick={() => setShowResultsModal(false)}
                      className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-xl transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="px-8 border-b border-gray-100 flex gap-6">
                  <button
                    onClick={() => setActiveTab('results')}
                    className={`py-4 font-bold text-sm border-b-[3px] transition-all ${activeTab === 'results'
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                      }`}
                  >
                    نتائج الاختبارات
                  </button>
                  {bookingResults.ai_available !== false && (
                    <button
                      onClick={() => { setActiveTab('insights'); requestAIAnalysis(); }}
                      className={`py-4 font-bold text-sm border-b-[3px] transition-all flex items-center gap-2 ${activeTab === 'insights'
                        ? 'border-purple-600 text-purple-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                        }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      تحليل الذكاء الاصطناعي
                      {aiLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {bookingResults.ai_limit && bookingResults.ai_limit.limit > 0 && (
                        <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full">
                          {bookingResults.ai_limit.used}/{bookingResults.ai_limit.limit}
                        </span>
                      )}
                    </button>
                  )}
                </div>

                {/* Content Area */}
                <div className="p-8 bg-gray-50/50 min-h-[400px]">
                  {activeTab === 'insights' ? (
                    aiLoading ? (
                      <div className="text-center py-16">
                        <div className="w-12 h-12 border-3 border-purple-200 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
                        <h3 className="text-purple-600 font-semibold mb-1">جاري تحليل نتائجك...</h3>
                        <p className="text-sm text-gray-400">يستغرق بضع ثوانٍ فقط</p>
                      </div>
                    ) : aiError ? (
                      <div className="text-center py-12">
                        <AlertCircle className="w-10 h-10 text-red-300 mx-auto mb-3" />
                        <h3 className="text-red-500 font-semibold mb-1">{aiError}</h3>
                        <button onClick={requestAIAnalysis} className="mt-4 px-5 py-2 bg-purple-100 text-purple-600 rounded-xl text-sm font-semibold hover:bg-purple-200 transition-colors">
                          إعادة المحاولة
                        </button>
                      </div>
                    ) : bookingResults.ai_analysis ? (
                      <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-5"
                      >
                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 sm:p-6 rounded-2xl border border-purple-100">
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="bg-white p-2.5 rounded-xl shadow-sm hidden sm:block">
                              <Brain className="w-7 h-7 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="text-base font-bold text-purple-900 mb-2">تحليل صحي ذكي</h3>
                              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                                {bookingResults.ai_analysis}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-amber-50 border border-amber-100 p-3 rounded-xl flex gap-2.5">
                          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800">
                            تحليل تلقائي بالذكاء الاصطناعي — لا يغني عن استشارة الطبيب المختص.
                          </p>
                        </div>
                      </motion.div>
                    ) : (
                      <div className="text-center py-12">
                        <Sparkles className="w-10 h-10 text-purple-300 mx-auto mb-3" />
                        <h3 className="text-gray-500 font-semibold mb-2">تحليل نتائجك بالذكاء الاصطناعي</h3>
                        <p className="text-sm text-gray-400 mb-5">اضغط الزر لتحليل نتائجك والحصول على نصائح صحية مخصصة</p>
                        <button onClick={requestAIAnalysis} className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition-colors inline-flex items-center gap-2">
                          <Brain className="w-4 h-4" />
                          حلّل نتائجي
                        </button>
                      </div>
                    )
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-6"
                    >
                      {bookingResults.tests?.map((test, idx) => {
                        const testName = test.lab_test_id?.name_ar || test.lab_test_id?.name_en || 'تحليل';
                        const results = test.results || [];
                        const isSerialChart = test.print_layout_style === 'serial_with_chart' && test.serial_results?.length > 0;

                        // ─── Serial With Chart (SIBO etc.) ───
                        if (isSerialChart) {
                          return <SerialChartTest key={idx} test={test} testName={testName} />;
                        }

                        return (
                          <div key={idx} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                            {/* Test Header */}
                            <div className="px-6 py-4 bg-slate-50 border-b border-gray-100 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-teal-600">
                                  <FlaskConical className="w-5 h-5" />
                                </div>
                                <div>
                                  <h3 className="font-bold text-slate-800">{testName}</h3>
                                  {test.description && (
                                    <p className="text-xs text-slate-400 mt-0.5">{test.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Detailed Info Section (Collapsible or visible) */}
                            {(test.why_test || test.high_result_meaning) && (
                              <div className="px-6 py-3 bg-blue-50/50 border-b border-blue-100/50 text-sm text-slate-600">
                                {test.why_test && (
                                  <div className="mb-2">
                                    <span className="font-bold text-blue-700">لماذا هذا الفحص؟:</span> {test.why_test}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Results List */}
                            <div className="p-6 space-y-8">
                              {results.map((result, rIdx) => {
                                const refRange = result.reference_range;
                                const resultStatus = getResultStatus(result.value, result.low_value, result.high_value, result.flag);
                                const position = calculatePosition(result.value, result.low_value, result.high_value);
                                const hasNumericValue = !isNaN(parseFloat(result.value)) && result.low_value !== undefined && result.high_value !== undefined;
                                const compId = result.component_id?.name; // Simple unique key replacement for now

                                return (
                                  <div key={rIdx} className={rIdx > 0 ? 'pt-8 border-t border-gray-50' : ''}>
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                                      <div>
                                        <h4 className="font-bold text-slate-700 text-lg mb-1 flex items-center gap-2">
                                          {result.component_id?.name}
                                          {/* History Button */}
                                          {result.history && result.history.length > 0 && (
                                            <button
                                              onClick={() => setExpandedHistory(expandedHistory === compId ? null : compId)}
                                              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all border ${expandedHistory === compId
                                                ? 'bg-teal-50 text-teal-700 border-teal-200'
                                                : 'bg-white text-slate-500 border-slate-200 hover:border-teal-300 hover:text-teal-600'
                                                }`}
                                            >
                                              <BarChart2 className="w-3.5 h-3.5" />
                                              <span>{expandedHistory === compId ? 'إخفاء السجل' : 'سجل النتائج'}</span>
                                            </button>
                                          )}
                                        </h4>
                                        <div className="flex items-center gap-3 text-sm text-slate-400 font-medium">
                                          <span>الوحدة: {result.unit || '-'}</span>
                                          <span>•</span>
                                          <span>المعدل الطبيعي: {refRange || '-'}</span>
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-4">
                                        <span className={`px-3 py-1 rounded-lg text-sm font-bold flex items-center gap-2 ${resultStatus.bgColor} ${resultStatus.color}`}>
                                          {resultStatus.status === 'high' && <TrendingUp className="w-4 h-4" />}
                                          {resultStatus.status === 'low' && <TrendingDown className="w-4 h-4" />}
                                          {resultStatus.status === 'normal' && <CheckCircle className="w-4 h-4" />}
                                          {resultStatus.label}
                                        </span>
                                        <span className={`text-3xl font-bold ${resultStatus.color}`}>
                                          {result.value}
                                        </span>
                                      </div>
                                    </div>

                                    {/* History Chart */}
                                    <AnimatePresence>
                                      {expandedHistory === compId && result.history && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: 'auto', opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          className="overflow-hidden mb-6"
                                        >
                                          <div className="w-full max-w-sm mx-auto"> {/* Constrain width */}
                                            <div className="h-48 bg-white border border-gray-100 rounded-xl p-4 mt-2">
                                              <p className="text-sm font-bold text-slate-700 mb-4 text-center">تطور النتائج السابقة</p>
                                              <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart
                                                  data={[...result.history, { date: bookingResults.booking_date, value: result.value }].sort((a, b) => new Date(a.date) - new Date(b.date))}
                                                  margin={{ top: 20, right: 30, left: 30, bottom: 0 }}
                                                >
                                                  <defs>
                                                    <linearGradient id={`colorValue-${rIdx}`} x1="0" y1="0" x2="0" y2="1">
                                                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                    </linearGradient>
                                                  </defs>
                                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                  <XAxis
                                                    dataKey="date"
                                                    tickFormatter={(date) => new Date(date).toLocaleDateString('ar-SA-u-ca-gregory', { day: 'numeric', month: 'short' })}
                                                    stroke="#94a3b8"
                                                    fontSize={12}
                                                    tickMargin={10}
                                                    interval="preserveStartEnd"
                                                  />
                                                  <YAxis hide domain={['auto', 'auto']} />
                                                  <Tooltip
                                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                                    labelFormatter={(date) => new Date(date).toLocaleDateString('ar-SA-u-ca-gregory', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                    cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '4 4' }}
                                                  />
                                                  <Area
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="#10b981"
                                                    strokeWidth={3}
                                                    fillOpacity={1}
                                                    fill={`url(#colorValue-${rIdx})`}
                                                    dot={{ r: 4, stroke: '#10b981', strokeWidth: 2, fill: '#fff' }}
                                                    activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2, fill: '#10b981' }}
                                                  >
                                                    <LabelList
                                                      dataKey="value"
                                                      position="top"
                                                      offset={10}
                                                      fill="#10b981"
                                                      fontSize={12}
                                                      fontWeight="bold"
                                                      formatter={(val) => Number(val).toFixed(2)}
                                                    />
                                                  </Area>
                                                </AreaChart>
                                              </ResponsiveContainer>
                                            </div>
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>

                                    {/* Visual Scale */}
                                    {hasNumericValue && (
                                      <div className="mt-4" dir="ltr">
                                        <div className="relative h-4 bg-gray-100 rounded-full overflow-visible mb-2">
                                          {/* Gradient Background - High(Left) to Low(Right) */}
                                          <div className="absolute inset-0 flex rounded-full overflow-hidden opacity-90">
                                            <div className="w-[20%] bg-red-300" /> {/* High Critical */}
                                            <div className="w-[15%] bg-orange-200" /> {/* High */}
                                            <div className="w-[30%] bg-green-300" /> {/* Normal */}
                                            <div className="w-[15%] bg-orange-200" /> {/* Low */}
                                            <div className="w-[20%] bg-red-300" /> {/* Low Critical */}
                                          </div>

                                          {/* Marker - White Circle */}
                                          <motion.div
                                            initial={{ left: '50%' }}
                                            animate={{ left: `${position}%` }}
                                            transition={{ duration: 1, type: 'spring' }}
                                            className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-4 border-slate-700 rounded-full shadow-lg z-10 box-border"
                                            style={{ marginLeft: '-12px' }}
                                          />
                                        </div>

                                        {/* Scale Labels */}
                                        <div className="flex justify-between text-[10px] text-gray-500 font-medium px-1">
                                          <span>مرتفع جداً</span>
                                          <span className="mr-6">مرتفع</span>
                                          <span>طبيعي</span>
                                          <span className="ml-6">منخفض</span>
                                          <span>منخفض جداً</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PatientResults;
